﻿import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { Button, Card, FIELD_CLASS } from '@/components/ui';
import { MoneyInput } from '@/components/MoneyInput';
import { formatDate, formatKrw } from '@/lib/format';
import type { ApiException } from '@/lib/api/types';
import { usePaperComparison } from './usePaper';
import { CorpActionPanel } from './CorpActionPanel';

/**
 * AI 모의투자 새 회차 시작. 관리자 화면에 붙는다. (18단계 ⑧, docs/impl/88)
 *
 * 🔴 이름은 "초기화" 지만 지우지 않는다 (2026-08-30)
 *   보유를 청산해 지난 회차 성적을 확정하고 계좌를 새 시드로 되돌릴 뿐이다.
 *   체결·곡선·회고는 회차별로 남는다. 확인 문구는 그대로 두었다 -
 *   보유가 전량 청산되는 것은 여전히 되돌릴 수 없다.
 *   🔴 3회차 종료부터(U-31)는 청산도 되돌림도 없다 - 성적표를 적고 그 순간 평가액에서 새로 출발한다.
 *     그래도 확인 문구는 둔다. 회차를 닫는 것은 되돌릴 수 없다.
 *
 * 🔴 여섯 계좌가 항상 함께 시작한다
 *   계좌를 고르는 선택지를 두지 않는다. 하나만 되돌리면
 *   시작일이 다른 곡선들을 같은 축에 겹쳐 놓게 되고
 *   비교 탭이 조용히 거짓을 말하기 시작한다. 화면에는 아무 표시도 안 난다.
 *
 * 🔴 확인 문구를 손으로 입력하게 한다
 *   예/아니오 버튼만으로는 부족하다. 되돌릴 수 없는 작업이고,
 *   관리자 화면을 열어둔 채 잘못 누르는 일이 실제로 생긴다.
 *   옮겨 적게 만들면 무엇을 지우는지 한 번 읽게 된다.
 */

const CONFIRM_PHRASE = '모의투자 초기화';

interface ResetResult {
  profile: string;
  profileLabel: string;
  seed: number;
  removedTrades: number;
}

/**
 * 관리자 › 모의투자 탭 전체. (2026-10-11 — 기업행위 의심 패널을 더하며 감쌌다)
 *
 * 탭은 공통 영역(features/admin/AdminPage)이 이 컴포넌트 하나를 그린다. 탭에 패널을 더할 때
 * 공통 파일을 고치지 않도록 trading 쪽인 여기서 묶는다(CLAUDE.md 4.2 · impl README ② 공유 파일).
 * 기업행위 의심을 위에 둔다 — 정지된 종목이 있으면 먼저 보여야 한다.
 */
export function PaperResetPanel() {
  return (
    <div className="flex flex-col gap-4">
      <CorpActionPanel />
      <RoundStartCard />
    </div>
  );
}

function RoundStartCard() {
  /*
    🔴 회차 방식은 서버 설정이 정한다 (U-31, 2026-09-25 · paper.round.end-liquidate)
      보유를 넘기는 방식이면 서버가 시드 지정을 거부한다 - 현금을 되돌리지 않으므로 뜻이 없다.
      그래서 그때는 시드 칸을 아예 그리지 않는다. 비교 탭과 같은 조회를 써서 캐시를 나눠 쓴다.
      아직 모르면(로딩) 시드 칸을 숨긴다 - 기본값이 넘기는 방식이다.
  */
  const comparison = usePaperComparison();
  const carry = comparison.data?.round?.carryHoldings ?? true;
  const [confirm, setConfirm] = useState('');
  const [seed, setSeed] = useState('');
  const [endDate, setEndDate] = useState('');
  const [done, setDone] = useState<ResetResult[] | null>(null);
  const queryClient = useQueryClient();

  const reset = useMutation<ResetResult[], ApiException>({
    mutationFn: () =>
      api.post<ResetResult[]>('/admin/trading/paper/reset', {
        confirm,
        // 비우면 백엔드가 paper.seed 설정값을 쓴다.
        seed: carry || seed.trim() === '' ? null : Number(seed.replace(/,/g, '')),
        // 비우면 기한 없이 굴린다 (2026-08-30)
        plannedEndDate: endDate.trim() === '' ? null : endDate,
      }),
    onSuccess: (results) => {
      setDone(results);
      setConfirm('');
      setSeed('');
      setEndDate('');
      // 모의투자 화면이 캐시를 들고 있다. 초기화 후 옛 숫자가 남으면 안 된다.
      queryClient.invalidateQueries({ queryKey: ['trading', 'paper'] });
      // 🔴 저점매수는 캐시 키가 따로다 (2026-08-28). 빠뜨리면 그 탭만
      //   지워진 보유를 계속 보여준다 - 화면에는 아무 이상이 없어 보인다.
      queryClient.invalidateQueries({ queryKey: ['trading', 'band'] });
    },
  });

  const ready = confirm === CONFIRM_PHRASE && !reset.isPending;

  /*
    🔴 기본은 접어 둔다 (2026-09-29 I-9 · OQ D-2)
      8/19~8/23 스냅샷이 이 버튼(그때는 전부 삭제)으로 날아갔고, 사용자가 "앞으로 초기화 안 쓴다"(9/6)고 했다.
      지금 서버는 아무것도 지우지 않는다(8/30 부터 회차 시작 · deleteAllByAccountId 호출처 0) — 그래도 남은 위험이 있다:
      🔴 진행 중인 회차가 있을 때 누르면 그 회차가 **예정일 전에 오늘로 끝난다**(성적표가 오늘 값으로 적힌다).
      4회차는 3회차가 끝난 뒤(10/16) 이 패널로 시작해야 해서 없애지 않고 접는다. 펼치면 진행 중 회차를 먼저 경고한다.
  */
  const [open, setOpen] = useState(false);
  const round = comparison.data?.round ?? null;
  const running = round?.running ?? false;

  if (!open && !done) {
    return (
      <Card title="AI 모의투자 새 회차 시작">
        <p className="text-sm text-fg-muted">
          {/* 로딩 중에 "진행 중인 회차가 없습니다" 가 잠깐 보이면 틀린 말이다 — 중립 문구를 둔다 */}
          {comparison.isLoading
            ? '회차 정보를 불러오는 중입니다.'
            : running
            ? `${round?.roundNo}회차가 진행 중입니다${round?.plannedEndDate ? ` (종료 예정 ${formatDate(round.plannedEndDate)})` : ''}. ` +
              '회차는 예정일 20:40에 저절로 끝나므로, 새 회차를 시작할 때만 펼칩니다.'
            : '진행 중인 회차가 없습니다. 새 회차를 시작하려면 펼칩니다.'}
        </p>
        <div className="mt-3">
          <Button variant="secondary" onClick={() => setOpen(true)}>
            펼치기
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="AI 모의투자 새 회차 시작">
      <div className="space-y-3">
        {running && (
          <p className="rounded-lg border border-danger bg-badge-danger-bg p-3 text-sm font-medium text-badge-danger-fg">
            🔴 {round?.roundNo}회차가 진행 중입니다
            {round?.plannedEndDate ? `(종료 예정 ${formatDate(round.plannedEndDate)})` : ''}. 지금 새 회차를 시작하면{' '}
            {round?.roundNo}회차가 예정일 전에 <strong>오늘로 끝나고</strong>, 성적표도 오늘 값으로 적힙니다.
          </p>
        )}
        <div className="rounded-lg border border-danger/40 bg-badge-danger-bg p-3 text-sm text-badge-danger-fg">
          <p className="font-medium">되돌릴 수 없습니다.</p>
          {carry ? (
            <p className="mt-1 text-xs">
              지금 회차를 끝내고 <strong>새 회차</strong>를 시작합니다. 보유 종목은{' '}
              {/* ✏️ 9/28 I-78 2차 — 실시간 값이 아니라 그날까지 받아 둔 가장 최근 일봉 종가다(RoundEquityService) */}
              <strong>팔지 않고 넘깁니다.</strong> 진행 중인 회차가 있으면 그날까지 받아 둔 가장 최근 일봉
              종가로 평가해 성적표를 적고(저녁 20:25 확정 봉 전이면 잠정값이나 전날 값일 수 있습니다), 그
              평가액(현금 + 보유)이 새 회차의 출발점이 됩니다. 현금 · 시드는 되돌리지 않습니다.
            </p>
          ) : (
            <p className="mt-1 text-xs">
              지금 회차를 끝내고 <strong>새 회차</strong>를 시작합니다. 보유 종목을{' '}
              <strong>현재가로 전량 청산</strong>해 성적을 확정한 뒤 예수금이 새 시드로 돌아갑니다.
            </p>
          )}
          <p className="mt-1 text-xs">
            📌 <strong>과거 기록은 지워지지 않습니다.</strong> 지난 회차의 체결 · 곡선 · 회고는
            그대로 남고, 화면이 새 회차 구간만 보여줍니다.
          </p>
          <p className="mt-1 text-xs">
            🔴 모든 계좌가 <strong>항상 함께</strong> 시작합니다. 하나만 되돌리면 시작일이
            어긋나 비교가 성립하지 않기 때문에 선택지를 두지 않았습니다.
          </p>
        </div>

        {!carry && (
          <label className="block">
            <span className="text-xs text-fg-muted">
              새 시드 (비우면 설정값 2,000만원 — paper.seed)
            </span>
            {/* 콤마 자동 입력 (2026-08-20 전수조사). 제출부는 전부터 콤마를 벗겨 왔다 */}
            <MoneyInput
              value={seed}
              onChange={setSeed}
              placeholder="20,000,000"
              aria-label="새 시드 금액"
            />
          </label>
        )}

        <label className="block">
          <span className="text-xs text-fg-muted">
            종료 예정일 (비우면 기한 없이 굴립니다)
          </span>
          {/*
            그 날 20:40 배치가 회차를 닫는다 (애프터마켓 20:00 마감 뒤, I-52).
            옛 방식은 보유를 전량 청산하고 모든 계좌가 쉰다. 보유를 넘기는 방식(U-31)은 그날 종가로
            성적표를 적고, 새 회차를 시작할 때까지 새로 사지 않고 매도만 돈다.
          */}
          <input
            type="date"
            className={FIELD_CLASS}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label="실험 종료 예정일"
          />
        </label>

        <label className="block">
          <span className="text-xs text-fg-muted">
            확인 문구 — <code>{CONFIRM_PHRASE}</code> 를 그대로 입력
          </span>
          <input
            className={FIELD_CLASS}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={CONFIRM_PHRASE}
          />
        </label>

        <div className="flex flex-wrap gap-2">
          <Button variant="danger" disabled={!ready} onClick={() => reset.mutate()}>
            {reset.isPending ? '시작하는 중' : '새 회차 시작'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(false);
              setConfirm('');
            }}
          >
            접기
          </Button>
        </div>

        {reset.error && <p className="text-sm text-danger">{reset.error.message}</p>}

        {done && (
          <div className="rounded-lg border border-line p-3 text-sm">
            <p className="font-medium">새 회차를 시작했습니다.</p>
            <ul className="mt-1 space-y-0.5 text-xs text-fg-muted">
              {done.map((r) => (
                <li key={r.profile}>
                  {/* 보유를 넘기는 방식이면 서버가 seed 자리에 출발 평가액을 싣는다 (U-31) */}
                  {r.profileLabel} · {carry ? '출발 평가액' : '시드'} {formatKrw(r.seed)} ·{' '}
                  {carry ? `체결 · 포지션 기록 ${r.removedTrades}건 (보존됨)` : `지난 회차 체결 ${r.removedTrades}건 (보존됨)`}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
